var pt = pt || {};
pt.blocografico = pt.blocografico || {};
pt.blocografico.library = pt.blocografico.library || {};
pt.blocografico.library.createCarousel = pt.blocografico.library.createCarousel || {};

/**
 * Creates an infinite-loop carousel with optional autoplay, smooth animation, and clickable dots.
 *
 * @param {string} selector - CSS selector for the carousel wrapper element (e.g. "#hero-slider").
 *   The wrapper must contain:
 *     - a <ul class="carousel"> with <li class="card" data-slider-index="N"> children
 *     - a <ul class="dots"> with <li class="dot"> children (one per real card)
 *
 * @param {object} [options={}]
 * @param {boolean} [options.autoplay=true]
 *   Whether the carousel advances automatically.
 *
 * @param {number} [options.interval=2500]
 *   Time in milliseconds between each auto-advance. Only used when autoplay is true.
 *
 * @param {boolean} [options.clickable=false]
 *   When true, clicking a dot navigates instantly to that slide and shows a pointer cursor.
 *
 * @param {number} [options.smooth=0]
 *   Duration in milliseconds of the JS-driven ease-out animation for autoplay transitions.
 *   - 0  → instant jump (CSS scroll-behavior handles any visual smoothing)
 *   - N  → animated over N ms using an ease-out cubic curve (browser CSS is bypassed)
 *   Recommended range: 300–800 ms. Does not affect drag or dot-click navigation.
 *
 * @example
 * pt.blocografico.library.createCarousel("#hero-slider", {
 *   autoplay: true,
 *   interval: 3000,
 *   clickable: true,
 *   smooth: 500,
 * });
 */
pt.blocografico.library.createCarousel = function (selector, options = {}) {
  const { autoplay = true, interval = 2500, clickable = false, smooth = 0 } = options;

  const wrapper = document.querySelector(selector);
  const carousel = wrapper.querySelector(".carousel");
  const firstCardWidth = carousel.querySelector(".card").offsetWidth;
  const carouselChildrens = [...carousel.children];
  const carouselDots = wrapper.querySelectorAll(".dot");

  let isDragging = false,
    isAutoPlay = autoplay,
    isAnimating = false,
    teleportDelta = 0,
    startX,
    startScrollLeft,
    timeoutId;

  let cardPerView = Math.round(carousel.offsetWidth / firstCardWidth);

  carouselChildrens
    .slice(-cardPerView)
    .reverse()
    .forEach((card) => {
      carousel.insertAdjacentHTML("afterbegin", card.outerHTML);
    });

  carouselChildrens.slice(0, cardPerView).forEach((card) => {
    carousel.insertAdjacentHTML("beforeend", card.outerHTML);
  });

  carousel.classList.add("no-transition");
  carousel.scrollLeft = carousel.offsetWidth;
  carousel.classList.remove("no-transition");

  const teleport = (newLeft) => {
    if (isAnimating) teleportDelta += newLeft - carousel.scrollLeft;
    carousel.classList.add("no-transition");
    carousel.scrollLeft = newLeft;
    carousel.classList.remove("no-transition");
  };

  const autoScrollTo = (left) => {
    if (smooth === 0) {
      carousel.scrollLeft = left;
      return;
    }
    isAnimating = true;
    teleportDelta = 0;
    carousel.classList.add("js-animating");
    const start = carousel.scrollLeft;
    const distance = left - start;
    const startTime = performance.now();
    const step = (now) => {
      const progress = Math.min((now - startTime) / smooth, 1);
      const ease = 1 - Math.pow(1 - progress, 3);
      carousel.scrollLeft = start + distance * ease + teleportDelta;
      if (progress < 1) {
        requestAnimationFrame(step);
      } else {
        carousel.classList.remove("js-animating");
        isAnimating = false;
        teleportDelta = 0;
        if (!wrapper.matches(":hover") && isAutoPlay) autoPlay();
      }
    };
    requestAnimationFrame(step);
  };

  const dragStart = (e) => {
    isDragging = true;
    carousel.classList.add("dragging");
    startX = e.pageX;
    startScrollLeft = carousel.scrollLeft;
  };

  const dragging = (e) => {
    if (!isDragging) return;
    carousel.scrollLeft = startScrollLeft - (e.pageX - startX);
  };

  const dragStop = () => {
    isDragging = false;
    carousel.classList.remove("dragging");
  };

  const infiniteScroll = () => {
    if (carousel.scrollLeft === 0) {
      teleport(carousel.scrollWidth - 2 * carousel.offsetWidth);
    } else if (Math.ceil(carousel.scrollLeft) === carousel.scrollWidth - carousel.offsetWidth) {
      teleport(carousel.offsetWidth);
    }

    updateActiveSlide();

    if (!isAnimating) {
      clearTimeout(timeoutId);
      if (!wrapper.matches(":hover") && isAutoPlay) autoPlay();
    }
  };

  const autoPlay = () => {
    timeoutId = setTimeout(() => {
      autoScrollTo(carousel.scrollLeft + firstCardWidth);
    }, interval);
  };

  const updateActiveSlide = () => {
    const carouselRealChildrens = [...carousel.children];
    const firstCarRealWidth = carousel.querySelector(".card").offsetWidth;

    let activeIndex = Math.floor(carousel.scrollLeft / firstCarRealWidth) % carouselRealChildrens.length;
    activeIndex = isNaN(activeIndex) ? 0 : activeIndex;
    carouselRealChildrens.forEach((card, index) => {
      card.classList.toggle("active", index === activeIndex);
    });
    carouselDots.forEach((bullet) => bullet.classList.remove("active"));
    carouselDots[carouselRealChildrens[activeIndex].dataset.sliderIndex].classList.add("active");
  };

  carousel.addEventListener("mousedown", dragStart);
  carousel.addEventListener("mousemove", dragging);
  document.addEventListener("mouseup", dragStop);
  carousel.addEventListener("scroll", infiniteScroll);
  wrapper.addEventListener("mouseenter", () => clearTimeout(timeoutId));
  wrapper.addEventListener("mouseleave", () => {
    if (isAutoPlay) autoPlay();
  });

  if (isAutoPlay) autoPlay();

  if (clickable) {
    carouselDots.forEach((dot, index) => {
      dot.style.cursor = "pointer";
      dot.addEventListener("click", () => {
        carousel.scrollLeft = carousel.offsetWidth + index * firstCardWidth;
      });
    });
  }

  return {};
};
